import { useQuery } from '@apollo/client/react';
import {
  Badge,
  BodyText,
  Box,
  Button,
  DetailText,
  Flex,
  Heading,
  Link,
} from '@utilitywarehouse/hearth-react';
import { ShareSmallIcon } from '@utilitywarehouse/hearth-react-icons';
import clsx from 'clsx';
import { FC } from 'react';
import { useNavigate } from 'react-router-dom';

import { BundleBenefitsTile } from '@components/BundleBenefits';
import { BundleBot } from '@components/BundleBot';
import { DialogID, useDialog } from '@components/Dialog';
import { WelcomeBonusSection } from '@components/WelcomeBonusSection';
import { DISCOUNT_IDS } from '@constants/apiConstants';
import { CURRENCY_CODE } from '@constants/app';
import { MixpanelFlags } from '@constants/featureFlags';
import { paths } from '@constants/paths';
import { hasPrepaymentMeter } from '@features/EnergyTariffForm/utils';
import { UpdatedEnergyModal } from '@features/expiredQuotes/UpdatedEnergyModal';
import { useUpdatedEnergyModal } from '@features/expiredQuotes/useUpdatedEnergyModal';
import {
  BundleBenefit,
  ChargeAmount,
  EnergyQuoteType,
  GetBasketQuery,
  ProductType,
} from '@generated/gql/graphql';
import { useBundleBotPostAnimation } from '@hooks/useBundleBotPostAnimation';
import { useEnergyTariffsQuery } from '@hooks/useEnergyTariffsQuery';
import { useMixpanelFlag } from '@hooks/useMixpanelFlag';
import { useSaveForLater } from '@hooks/useSaveForLater';
import { useUpdatedTariffModalContext } from '@providers/basket/UpdatedTariffModalProvider/Context';
import { BundleBasketData } from '@src/types/bundle';
import { SelectedServices } from '@src/types/bundle';
import { useTrackingContext } from '@tracking/Provider/Context';
import { useTrackSignupBundleSelectedOnce } from '@tracking/trackingEvents';
import { bundleBotTextCheck } from '@utils/bundleBotText';
import { getCashbackSavingsValue } from '@utils/formatBasket/cashback';
import { selectedServicesToProductType } from '@utils/formatBasket/formatSelectedServices';
import { getInsuranceProductSubtype } from '@utils/insurance';
import { hasMultipleSimsSelected } from '@utils/mobile';
import { getMonthlyTotalAfterCashback } from '@utils/price';
import { getBundleBenefits } from '@utils/queries/bundleBenefitsQuery';

import { BundleAlert } from './BundleAlert';
import { BundleTile } from './BundleTile';
import { MobileTile } from './MobileTile';
import { WelcomeBonusDialog } from './WelcomeBonusDialog';
import './styles.scss';
import { useBundleValidation } from './useBundleValidations';

interface BundleProps {
  basket: BundleBasketData;
  basketData?: GetBasketQuery;
  selectedServices: SelectedServices;
}

const getWelcomeBonus = (benefits: BundleBenefit[]): string => {
  const wbAmount = benefits
    .map((benefit) => benefit.emphasisedText)
    .reduce((acc, text) => {
      // find the welcome bonus string and split out the amount
      // using a regexp capture group
      const match = text.match(/£(\d+) welcome bonus/);
      // return the capture group value, if it exists
      if (match) return match[1];
      return acc;
    }, '');

  return wbAmount;
};

const Bundle: FC<BundleProps> = ({ basket, basketData, selectedServices }) => {
  const navigate = useNavigate();
  const saveForLater = useSaveForLater();
  const { modalState, resetModalState } = useUpdatedTariffModalContext();
  const {
    loading: isEnergyTariffsLoading,
    tariffs,
    nextTariffs,
  } = useEnergyTariffsQuery();

  const welcomeBonusDialog = useDialog(DialogID.WELCOME_BONUS);

  const {
    energy,
    mobile,
    broadband,
    insurance,
    cashback,
    hasExpiredBundle,
    basketStatus,
    monthlyCost,
  } = basket;

  const {
    isOpen: isUpdateModalOpen,
    isLoading,
    closeModal,
    handleUpdateQuotes,
  } = useUpdatedEnergyModal(energy, hasExpiredBundle);

  const { trackingContexts } = useTrackingContext();
  const trackSignUpBundleSelectedOnce = useTrackSignupBundleSelectedOnce();

  const { showAlert, alertMessage, errorRef, validateSelection } =
    useBundleValidation(basketStatus);

  const { data: bundleBenefitsData } = useQuery(getBundleBenefits, {
    variables: { basketID: basket.id },
  });
  const bundleBenefits = bundleBenefitsData?.bundleBenefits;
  const welcomeBonus = getWelcomeBonus(bundleBenefits?.benefits || []);

  const handleNext = (welcomeBonusSkipped?: boolean) => {
    if (!validateSelection()) return;

    if (welcomeBonus && !cashback.selected && !welcomeBonusSkipped)
      return welcomeBonusDialog.setOpen(true);
    trackSignUpBundleSelectedOnce({}, { contexts: trackingContexts });
    navigate(paths.confirm);
  };

  const broadbandAvailable = broadband.available;
  const broadbandSelected = !!broadband.yourService;
  const mobileSelected = (mobile?.selectedSimCount ?? 0) > 0;
  const hasMoreThanOneMobileSelected = mobile
    ? hasMultipleSimsSelected(mobile)
    : false;
  const insuranceSelected = insurance.selected;

  const bhcPromo =
    insurance.boilerInsurance.discount?.qualified &&
    insurance.boilerInsurance.discount?.id ===
      DISCOUNT_IDS.INSURANCE_BOILER_THREE_MONTHS_FREE_DISCOUNT;

  const cashbackSavingsValue = getCashbackSavingsValue(
    basketData?.cashbackCalculatorDetails ?? null
  );

  const { value: bundleTileCopyVariant } = useMixpanelFlag(
    MixpanelFlags.BUNDLE_TILE_COPY,
    'old'
  );
  const bundleBotText = bundleBotTextCheck(basket, nextTariffs);
  const showBundleBot = !!(
    bundleBotText && !(energy && hasPrepaymentMeter(energy.details))
  );
  const { handleAnimationComplete } = useBundleBotPostAnimation(
    'bundlePage',
    showBundleBot
  );

  const holdBundleBot = isUpdateModalOpen || isLoading;
  const hasBenefits = bundleBenefits && bundleBenefits.benefits.length > 0;
  const selectedServiceCount =
    Object.values(selectedServices).filter(Boolean).length;

  return (
    <>
      <Flex className={clsx('wrapper', 'bundle')} direction="column">
        {showBundleBot && !holdBundleBot && (
          <BundleBot
            text={bundleBotText}
            onComplete={handleAnimationComplete}
          />
        )}

        <Flex className="bundle__section" direction="column">
          <Heading as="h3" size="lg" data-testid="bundles-page-heading">
            Services
          </Heading>
          <BundleAlert
            showAlert={showAlert}
            alertMessage={alertMessage}
            errorRef={errorRef}
          />
          <div
            className={clsx('bundle__services', {
              'bundle__services__mobile-badge': hasMoreThanOneMobileSelected,
              'bundle__services--has-badge':
                hasMoreThanOneMobileSelected || bhcPromo,
            })}
            data-testid="bundles-page-service-section"
          >
            <BundleTile
              productType={ProductType.Energy}
              price={energy?.selectedQuote?.monthlyCost as ChargeAmount}
              isSelected={!!energy?.selectedQuote}
              showCheaperBadge={
                energy ? broadbandSelected || mobileSelected : false
              }
              data-testid="bundles-page-energy-tile"
              disabled={!energy}
              compact
            />
            <BundleTile
              productType={ProductType.Broadband}
              price={broadband.monthlyCost}
              isSelected={broadbandSelected}
              disabled={!broadbandAvailable}
              tbybEligible={broadband.tbybEligible}
              bundleCopyVariant={bundleTileCopyVariant}
              discount={
                broadband.tbybActive
                  ? {
                      price: { value: '0', currency: CURRENCY_CODE },
                      durationMonths: 6,
                    }
                  : null
              }
              compact
            />
            <MobileTile
              mobile={mobile}
              bundleCopyVariant={bundleTileCopyVariant}
            />
            {insurance.available && (
              <>
                {bhcPromo ? (
                  <label className="bundle-tile__container">
                    <Badge
                      className="bundle-tile__two-services-badge"
                      size="sm"
                      variant="subtle"
                      colorScheme="insurance"
                    >
                      3 months free boiler cover
                    </Badge>
                    <BundleTile
                      productType={ProductType.Insurance}
                      price={insurance.monthlyCost}
                      isSelected={insuranceSelected}
                      compact
                      discount={insurance.discount}
                      subType={getInsuranceProductSubtype({
                        boilerAvailable: insurance.boilerInsurance.available,
                        incomeAvailable: insurance.incomeProtector.available,
                      })}
                    />
                  </label>
                ) : (
                  <BundleTile
                    productType={ProductType.Insurance}
                    price={insurance.monthlyCost}
                    isSelected={insuranceSelected}
                    compact
                    subType={getInsuranceProductSubtype({
                      boilerAvailable: insurance.boilerInsurance.available,
                      incomeAvailable: insurance.incomeProtector.available,
                    })}
                  />
                )}
              </>
            )}
          </div>
        </Flex>
        {hasBenefits && (
          <Flex
            className="bundle__section"
            marginTop={{
              mobile: '200',
              desktop: '300',
            }}
          >
            <BundleBenefitsTile {...bundleBenefits} />
          </Flex>
        )}
        {cashback.available && (
          <Flex className="bundle__cashback-section" direction="column">
            <Heading
              as="h3"
              size="lg"
              data-testid="bundles-page-cbc-section-heading"
              marginBottom="200"
            >
              Shrink your bills even more...
            </Heading>
            <Box
              marginTop={{
                desktop: '300',
                mobile: '200',
              }}
            >
              <BundleTile
                productType={ProductType.CashbackCard}
                price={cashback.monthlyCost}
                isSelected={cashback.selected}
                discount={cashback.discount}
              />
            </Box>
          </Flex>
        )}
        <Flex className="bundle__welcome-bonus-section">
          <WelcomeBonusSection
            selectedServices={selectedServicesToProductType(selectedServices)}
            insuranceAvailable={insurance.available}
          />
        </Flex>
        {selectedServiceCount > 0 && (
          <Flex className={clsx('bundle__section', 'bundle__total')}>
            <BodyText
              as="span"
              weight="semibold"
              size="lg"
              data-testid="bundles-page-monthly-total"
            >
              Monthly total:{' '}
            </BodyText>
            <DetailText size="2xl">
              {getMonthlyTotalAfterCashback(monthlyCost, cashbackSavingsValue)}
            </DetailText>
          </Flex>
        )}
        <Flex
          className={clsx('bundle__section', 'bundle__actions')}
          justifyContent="between"
        >
          <Button
            disabled={selectedServiceCount === 0}
            onClick={() => handleNext(false)}
            data-testid="bundles-page-next-button"
          >
            Next
          </Button>
          {selectedServiceCount > 0 && (
            <Link
              data-testid="save-for-later-button"
              tabIndex={0}
              className="bundle__actions__link"
              asChild
            >
              <button type="button" onClick={saveForLater}>
                <ShareSmallIcon /> Share your quote
              </button>
            </Link>
          )}
        </Flex>
      </Flex>
      <WelcomeBonusDialog
        amount={welcomeBonus}
        onClose={() => handleNext(true)}
      />

      {energy && tariffs && (
        <UpdatedEnergyModal
          fuelType={energy.fuelType}
          modalState={modalState}
          tariffs={tariffs}
          initialQuoteType={
            energy.selectedQuote?.quoteType ?? EnergyQuoteType.Variable
          }
          selectedPrice={energy.selectedQuote?.monthlyCost ?? null}
          isOpen={isUpdateModalOpen}
          isLoading={isLoading || isEnergyTariffsLoading}
          onSubmit={(quoteType) =>
            handleUpdateQuotes(quoteType, () => {
              resetModalState();
              closeModal();
            })
          }
        />
      )}
    </>
  );
};

export { Bundle };
